import { ORIGIN_CITIES } from '~/constants/origin-cities';

type Origin = {
  id: number;
  locationName?: string;
  country?: { countryId: string; countryName: string } | null;
};

type JourneyItem = { type: string; originId?: number };

const knownOriginIds = new Set([...ORIGIN_CITIES.UK.map((c) => c.id), ...ORIGIN_CITIES.EU.map((c) => c.id)]);

export function getFirstLegOriginId(items: JourneyItem[]): number | undefined {
  return items.find((item) => item.type === 'Journey')?.originId;
}

/**
 * Detects whether a trip is a US trip based on available signals.
 *
 * Detection logic:
 * 1. Origins with country data are the most reliable signal.
 *    If any origin has countryId 'US', it's a US trip.
 *    If origins with country data exist but none are US, it's not a US trip.
 * 2. If no origins have country data, falls back to checking whether the
 *    originId is NOT in the known UK/EU origin set.
 */
export function isUSTrip(args: { origins?: Origin[]; originId?: number }): boolean {
  const { origins = [], originId } = args;

  const originsWithCountry = origins.filter((o) => o.country?.countryId);

  if (originsWithCountry.length > 0) {
    return originsWithCountry.some((o) => o.country?.countryId === 'US');
  }

  if (originId !== undefined && !knownOriginIds.has(originId)) {
    return true;
  }

  return false;
}

export function isUSReplanEnabled(): boolean {
  return typeof process !== 'undefined' && process.env.NEXT_PUBLIC_FF_US_REPLAN_ENABLED === 'true';
}

/**
 * Combined check: feature flag AND US trip detection for UI controls (e.g. dropdown visibility).
 * Only uses explicit country data from items or origins - never falls back to originId,
 * which can produce false positives for UK/EU trips with IDs not in the hardcoded list.
 */
export function isUSBlueprintTrip(args: {
  origins?: Origin[];
  originId?: number;
  items?: Array<{ country?: { countryId: string } | null }>;
}): boolean {
  if (!isUSReplanEnabled()) return false;
  if (args.items?.some((item) => item.country?.countryId === 'US')) return true;
  if (args.origins?.some((o) => o.country?.countryId === 'US')) return true;
  return false;
}

/**
 * Detects US trip for origin resolution in edit forms.
 * Looks up the trip's originId in the origins list and checks its country.
 * If the originId isn't found in the origins list, treats it as US.
 * Falls back to the hardcoded list only when the origins list is empty.
 */
export function isUSOrigin(args: { origins?: Origin[]; originId?: number }): boolean {
  const { origins = [], originId } = args;

  if (originId !== undefined && origins.length > 0) {
    const matched = origins.find((o) => o.id === originId);
    if (matched) {
      return matched.country?.countryId === 'US';
    }
    return true;
  }

  if (originId !== undefined && !knownOriginIds.has(originId)) {
    return true;
  }

  return false;
}
